/**
 * /common-procedures — hospital code lookup.
 *
 * A consumer holds a bill or an estimate with a code on it and wants to know
 * what hospitals list for it. This page answers that from the static price
 * index on the CDN (codes_stats_<family>.json + codes_index_<family>.json):
 * no Supabase call, works offline-first through loadStaticData().
 *
 * Figures per code follow the CPT card's language — cheapest 10% · MEDIAN
 * (lead) · average · priciest 10% · hospitals reporting. Median leads and the
 * average never stands alone (classroom FAQ 3): every average carries its gap
 * to the median beside it.
 *
 * Code-family colors come from ONE shared constant (src/lib/codeFamilies.ts)
 * so a CPT is blue here, on hospital profiles, on the rankings and in the
 * Dignity lessons alike.
 */
import { useState, useMemo, useEffect, useRef, useCallback, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Search, X, ChevronDown, ChevronUp, MapPin, Loader2, ArrowRight, Building2, RefreshCw, Navigation, CornerDownLeft } from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { loadStaticData } from '@/lib/offlineData';
import { loadCodeDetail, type CodeDetail, type CodeDetailHospital } from '@/lib/codeDetailStore';
import { CODE_FAMILIES, codeFamily, type CodeFamily, type CodeFamilyKey } from '@/lib/codeFamilies';
import { PriceRangeBand } from '@/pages/CPTCodes';
import { useUserLocation } from '@/hooks/useUserLocation';
import { haversineDistance } from '@/lib/scenarioCoverageStore';

// ── Static index shapes ──────────────────────────────────────────────────────
type LookupFamily = 'cpt' | 'hcpcs' | 'drg' | 'rev' | 'ndc';
const EAGER_FAMILIES: LookupFamily[] = ['cpt', 'hcpcs', 'drg', 'rev'];
const FAMILY_ORDER: LookupFamily[] = ['cpt', 'hcpcs', 'drg', 'rev', 'ndc'];

interface CodeStat { med?: number; p10?: number; p90?: number; avg?: number; th?: number; n?: string }
/** Site-wide coverage stamped into every codes_stats file by the generator:
 *  distinct hospitals we hold a published price file for (all of
 *  chargemaster_files — not the ranked subset) and the states they sit in. */
interface StatsCoverage { hospitals?: number; states?: number; dc?: boolean; territories?: string[] }
interface StatsFile { generated_at?: string; count?: number; coverage?: StatsCoverage; data: Record<string, CodeStat> }
interface IndexRow { code: string; desc?: string; cn?: string; h?: number }
interface IndexFile { generated_at?: string; count?: number; data: IndexRow[] }

export interface CodeRow {
  family: LookupFamily;
  /** Display + link code. DRG is the bare number ('470'); NDC keeps its dashes. */
  code: string;
  key: string;
  /** Consumer name (AI, from hospital wording) or a cleaned descriptor. */
  name: string;
  /** "How hospitals list it" — the most common chargemaster wording. */
  wording?: string;
  th: number;
  p10?: number; med?: number; avg?: number; p90?: number;
  // search helpers
  codeCompact: string;   // lowercase, no dashes/spaces
  codeStripped: string;  // codeCompact without leading zeros
  searchName: string;
  searchWording: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtUsd = (v?: number | null) => (v && v > 0 ? '$' + Math.round(v).toLocaleString() : '—');
const fmtInt = (v?: number | null) => (v != null ? Math.round(v).toLocaleString() : '—');

function titleCase(s: string): string {
  return s.toLowerCase().replace(/\b([a-z])/g, m => m.toUpperCase()).replace(/\bAnd\b/g, 'and').replace(/\bOf\b/g, 'of').replace(/\bOr\b/g, 'or');
}

/** Strip a leading "0300 - " / "177 - " / "50242-0041-64 - " code echo from an index descriptor. */
function stripCodeEcho(desc: string): string {
  return desc.replace(/^\s*[A-Z0-9-]{3,14}\s*-\s+/i, '').trim();
}

/** At least three letters in a row somewhere — filters out code-echo / SKU strings. */
function hasWords(s: string): boolean { return /[a-z]{3,}/i.test(s); }

function cleanRevName(desc?: string): string {
  if (!desc) return '';
  const body = stripCodeEcho(desc);
  return body ? titleCase(body) : '';
}

function buildRows(family: LookupFamily, stats: StatsFile | null, index?: IndexFile | null): CodeRow[] {
  if (!stats?.data) return [];
  const idx = new Map<string, IndexRow>();
  if (index?.data) {
    for (const r of index.data) {
      // DRG index rows are 'MS-470'; stats keys are bare '470'. Key both ways.
      idx.set(r.code, r);
      if (family === 'drg') idx.set(r.code.replace(/^MS-/i, '').replace(/^0+/, '') || '0', r);
    }
  }
  const fam = codeFamily(family);
  const rows: CodeRow[] = [];
  for (const [code, st] of Object.entries(stats.data)) {
    if (!st || !(st.th && st.th > 0)) continue;
    const ir = idx.get(code);
    let name = (st.n || '').trim();
    let wording = ir?.desc ? stripCodeEcho(ir.desc) : '';
    if (!name && ir?.cn) name = ir.cn.trim();
    if (!name && family === 'rev') name = cleanRevName(ir?.desc);
    if (!name && family === 'ndc' && wording) { name = wording; wording = ''; }
    if (!name || !hasWords(name)) name = `${fam.label} ${code}`;
    if (wording && (wording.toLowerCase() === name.toLowerCase() || !hasWords(wording))) wording = '';
    const compact = code.toLowerCase().replace(/[\s.-]/g, '');
    rows.push({
      family, code, key: `${family}:${code}`, name, wording: wording || undefined,
      th: st.th, p10: st.p10, med: st.med, avg: st.avg, p90: st.p90,
      codeCompact: compact,
      codeStripped: compact.replace(/^0+/, '') || compact,
      searchName: name.toLowerCase(),
      searchWording: (wording || '').toLowerCase(),
    });
  }
  rows.sort((a, b) => b.th - a.th);
  return rows;
}

// ── Forgiving query parsing ──────────────────────────────────────────────────
interface ParsedQuery { family: LookupFamily | null; text: string; codeish: string | null; tokens: string[] }

const FAMILY_PREFIX: Array<[RegExp, LookupFamily]> = [
  [/^(cpt)\b/, 'cpt'], [/^(hcpcs)\b/, 'hcpcs'], [/^(ms-?drg|drg)\b/, 'drg'],
  [/^(revenue|rev)\b/, 'rev'], [/^(ndc|drugs?)\b/, 'ndc'],
];

function parseQuery(raw: string): ParsedQuery {
  let q = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  let family: LookupFamily | null = null;
  for (const [re, fam] of FAMILY_PREFIX) {
    const m = q.match(re);
    if (m) { family = fam; q = q.slice(m[0].length).replace(/^[\s:#-]+/, ''); break; }
  }
  const compact = q.replace(/[\s.#-]/g, '');
  // A code: optional letter, digits, optional letter/suffix — up to an 11-digit NDC.
  const codeish = compact.length > 0 && /^[a-z]?\d{1,11}[a-z]?$/.test(compact) ? compact : null;
  const tokens = q.split(' ').filter(Boolean);
  return { family, text: q, codeish, tokens };
}

function scoreRow(r: CodeRow, p: ParsedQuery): number {
  if (p.family && r.family !== p.family) return 0;
  if (p.codeish) {
    const q = p.codeish;
    const qs = q.replace(/^0+/, '') || q;
    if (r.codeCompact === q) return 100;
    if (r.codeStripped === qs) return 95;
    if (r.codeCompact.startsWith(q)) return 80 - Math.min(9, r.codeCompact.length - q.length);
    if (qs.length >= 2 && r.codeStripped.startsWith(qs)) return 70 - Math.min(9, r.codeStripped.length - qs.length);
    return 0;
  }
  if (!p.tokens.length) return 1;
  const name = r.searchName, w = r.searchWording;
  if (name === p.text) return 100;
  // Tier + how widely reported + how much of the name the query covers, so
  // "knee replacement" ranks Total Knee Replacement / DRG 470 above the
  // longer revision codes that merely start with the phrase.
  let tier = 0;
  if (name.startsWith(p.text)) tier = 52;
  else if (name.includes(p.text)) tier = 50;
  else if (p.tokens.every(t => name.includes(t))) tier = 42;
  else if (w && w.includes(p.text)) tier = 30;
  else if (w && p.tokens.every(t => name.includes(t) || w.includes(t))) tier = 20;
  if (!tier) return 0;
  const popularity = Math.min(12, Math.log10(r.th + 1) * 3.3);
  const tightness = tier >= 42 ? 10 * Math.min(1, p.text.length / Math.max(1, name.length)) : 0;
  return tier + popularity + tightness;
}

function looksLikeNdc(p: ParsedQuery): boolean {
  if (p.family === 'ndc') return true;
  return !!p.codeish && /^\d{9,11}$/.test(p.codeish);
}

function codeHref(r: CodeRow): string {
  const fam = codeFamily(r.family);
  if (r.family === 'drg') return `${fam.route}/MS-${r.code.padStart(3, '0')}`;
  return `${fam.route}/${encodeURIComponent(r.code)}`;
}

/** "+31% vs median" — the FAQ-3 companion every average carries. */
function avgGap(avg?: number, med?: number): { text: string; tone: 'up' | 'flat' | 'down' } | null {
  if (!avg || !med || med <= 0) return null;
  const pct = (avg / med - 1) * 100;
  if (Math.abs(pct) < 5) return { text: '≈ median', tone: 'flat' };
  const sign = pct > 0 ? '+' : '−';
  return { text: `${sign}${Math.round(Math.abs(pct))}% vs median`, tone: pct > 0 ? 'up' : 'down' };
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

// ── Theme ────────────────────────────────────────────────────────────────────
function useLookupTheme() {
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.design === 'civic';
  return useMemo(() => ({
    isAhp, isDark,
    navy: 'var(--civic-navy)', red: 'var(--civic-red)', gold: 'var(--civic-gold)', cream: 'var(--civic-cream)',
    ink: isDark ? 'var(--civic-warm)' : 'var(--civic-ink)',
    muted: isDark ? 'rgba(var(--civic-warm-rgb),0.62)' : 'rgba(var(--civic-ink-rgb),0.62)',
    faint: isDark ? 'rgba(var(--civic-warm-rgb),0.4)' : 'rgba(var(--civic-ink-rgb),0.42)',
    cardBg: isDark ? '#0f1a2e' : '#FFFFFF',
    cardBorder: isDark ? 'var(--civic-navy-deep)' : 'rgba(var(--civic-navy-rgb),0.16)',
    pageBg: isAhp ? (isDark ? 'transparent' : 'var(--civic-cream)') : 'transparent',
    rowAlt: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(var(--civic-navy-rgb),0.03)',
    rowHover: isDark ? 'rgba(var(--civic-gold-rgb),0.10)' : 'rgba(var(--civic-gold-rgb),0.14)',
    cheapest: isDark ? '#5fc48a' : '#2e8b57',
    median: isDark ? '#E2C27A' : '#b8860b',
    average: isDark ? 'var(--civic-warm)' : 'var(--civic-ink)',
    priciest: isDark ? '#F08A96' : 'var(--civic-red)',
    serif: "'Instrument Serif', Georgia, serif",
  }), [isAhp, isDark]);
}
type LookupTheme = ReturnType<typeof useLookupTheme>;

/** Family chip — canonical hue as the tint/border, dark ink of the same hue for
 *  text on a light card, the swatch itself on dark. */
function FamilyChip({ fam, code, isDark, size = 'sm', title }: { fam: CodeFamily; code?: string; isDark: boolean; size?: 'sm' | 'md'; title?: string }) {
  const text = isDark ? fam.color : fam.ink;
  return (
    <span
      title={title || fam.longLabel}
      className={`inline-flex items-center gap-1.5 rounded-md font-bold tabular-nums whitespace-nowrap ${size === 'md' ? 'px-2.5 py-1 text-sm' : 'px-2 py-0.5 text-xs'}`}
      style={{ color: text, background: `${fam.color}${isDark ? '24' : '2e'}`, border: `1px solid ${fam.color}${isDark ? '80' : '99'}` }}
    >
      <span className="inline-block rounded-full" style={{ width: 7, height: 7, background: fam.color }} aria-hidden />
      {code ? <><span className="opacity-75 font-semibold">{fam.label}</span><span>{code}</span></> : fam.label}
    </span>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function CommonProcedures() {
  const brand = useBrand();
  const { t } = useTranslation(['procedures', 'common']);
  const th = useLookupTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Query state — instant for the input, debounced for the 25K-row scan.
  const [query, setQuery] = useState(() => searchParams.get('q') || '');
  const [debounced, setDebounced] = useState(query);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 110);
    return () => clearTimeout(id);
  }, [query]);
  useEffect(() => {
    // Shareable searches: /common-procedures?q=99283. Replace, never push.
    const cur = searchParams.get('q') || '';
    const next = debounced.trim();
    if (cur === next) return;
    const sp = new URLSearchParams(searchParams);
    if (next) sp.set('q', next); else sp.delete('q');
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const [familyFilter, setFamilyFilter] = useState<LookupFamily | 'all'>(() => {
    const f = (searchParams.get('family') || '').toLowerCase();
    return (FAMILY_ORDER as string[]).includes(f) ? (f as LookupFamily) : 'all';
  });
  const [includeDrugs, setIncludeDrugs] = useState(false);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [showAll, setShowAll] = useState<Record<string, number>>({});

  const parsed = useMemo(() => parseQuery(debounced), [debounced]);
  const wantNdc = includeDrugs || looksLikeNdc(parsed) || familyFilter === 'ndc';

  // 1. Eager: stats for the four national families (+ the small REV index —
  //    revenue stats carry no consumer name, the index has the descriptor).
  const eager = useQuery({
    queryKey: ['code-lookup', 'eager'],
    queryFn: async () => {
      const [cpt, hcpcs, drg, rev, revIdx] = await Promise.all([
        loadStaticData<StatsFile>('codes_stats_cpt.json', { cdnOnly: true }),
        loadStaticData<StatsFile>('codes_stats_hcpcs.json', { cdnOnly: true }),
        loadStaticData<StatsFile>('codes_stats_drg.json', { cdnOnly: true }),
        loadStaticData<StatsFile>('codes_stats_rev.json', { cdnOnly: true }),
        loadStaticData<IndexFile>('codes_index_rev.json'),
      ]);
      if (!cpt && !hcpcs && !drg && !rev) throw new Error('price index unavailable');
      const rows = [
        ...buildRows('cpt', cpt), ...buildRows('hcpcs', hcpcs),
        ...buildRows('drg', drg), ...buildRows('rev', rev, revIdx),
      ];
      const generatedAt = cpt?.generated_at || hcpcs?.generated_at || drg?.generated_at || rev?.generated_at || null;
      const coverage: StatsCoverage | null = cpt?.coverage || hcpcs?.coverage || drg?.coverage || rev?.coverage || null;
      return { rows, generatedAt, coverage };
    },
    staleTime: Infinity,
    retry: 1,
  });

  // 2. Background: the larger indexes carry "how hospitals list it" wording and
  //    a fallback name for codes the stats file has no consumer name for.
  const wordings = useQuery({
    queryKey: ['code-lookup', 'wordings'],
    enabled: !!eager.data,
    queryFn: async () => {
      const [cpt, hcpcs, drg] = await Promise.all([
        loadStaticData<IndexFile>('codes_index_cpt.json'),
        loadStaticData<IndexFile>('codes_index_hcpcs.json'),
        loadStaticData<IndexFile>('codes_index_drg.json'),
      ]);
      const map = new Map<string, { wording: string; cn: string }>();
      const put = (fam: LookupFamily, f: IndexFile | null) => {
        for (const r of f?.data || []) {
          const code = fam === 'drg' ? (r.code.replace(/^MS-/i, '').replace(/^0+/, '') || '0') : r.code;
          map.set(`${fam}:${code}`, { wording: r.desc ? stripCodeEcho(r.desc) : '', cn: r.cn || '' });
        }
      };
      put('cpt', cpt); put('hcpcs', hcpcs); put('drg', drg);
      return map;
    },
    staleTime: Infinity,
    retry: 1,
  });

  // 3. On demand: drug NDCs (10MB of index — only when asked for, or when the
  //    query itself looks like an NDC).
  const ndc = useQuery({
    queryKey: ['code-lookup', 'ndc'],
    enabled: !!eager.data && wantNdc,
    queryFn: async () => {
      const [stats, index] = await Promise.all([
        loadStaticData<StatsFile>('codes_stats_ndc.json', { cdnOnly: true }),
        loadStaticData<IndexFile>('codes_index_ndc.json'),
      ]);
      return buildRows('ndc', stats, index);
    },
    staleTime: Infinity,
    retry: 1,
  });

  const rows = useMemo<CodeRow[]>(() => {
    if (!eager.data) return [];
    const base = eager.data.rows;
    const enriched = wordings.data
      ? base.map(r => {
          const w = wordings.data!.get(r.key);
          if (!w) return r;
          const name = r.name.startsWith(`${codeFamily(r.family).label} `) && w.cn ? w.cn : r.name;
          const wording = w.wording && w.wording.toLowerCase() !== name.toLowerCase() ? w.wording : undefined;
          if (name === r.name && wording === r.wording) return r;
          return { ...r, name, wording, searchName: name.toLowerCase(), searchWording: (wording || '').toLowerCase() };
        })
      : base;
    return wantNdc && ndc.data ? enriched.concat(ndc.data) : enriched;
  }, [eager.data, wordings.data, ndc.data, wantNdc]);

  // ── Matching ──
  const hasQuery = parsed.tokens.length > 0 || !!parsed.codeish;
  const matches = useMemo(() => {
    const out: Array<{ r: CodeRow; s: number }> = [];
    for (const r of rows) {
      if (familyFilter !== 'all' && r.family !== familyFilter) continue;
      const s = hasQuery ? scoreRow(r, parsed) : 1;
      if (s > 0) out.push({ r, s });
    }
    if (hasQuery) out.sort((a, b) => b.s - a.s || b.r.th - a.r.th);
    return out;
  }, [rows, parsed, familyFilter, hasQuery]);

  const grouped = useMemo(() => {
    const g = new Map<LookupFamily, CodeRow[]>();
    for (const { r } of matches) {
      let arr = g.get(r.family);
      if (!arr) { arr = []; g.set(r.family, arr); }
      arr.push(r);
    }
    return FAMILY_ORDER.filter(f => g.has(f)).map(f => ({ family: f, rows: g.get(f)! }));
  }, [matches]);

  const familyCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of rows) c[r.family] = (c[r.family] || 0) + 1;
    return c;
  }, [rows]);
  const familyMatchCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const { r } of matches) c[r.family] = (c[r.family] || 0) + 1;
    return c;
  }, [matches]);

  const suggestions = useMemo(() => (hasQuery ? matches.slice(0, 8).map(m => m.r) : []), [matches, hasQuery]);

  // ── Type-ahead ──
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  useEffect(() => { setHighlight(-1); }, [debounced]);
  const dropdownOpen = focused && hasQuery && suggestions.length > 0 && query.trim() !== '';

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { setFocused(false); inputRef.current?.blur(); return; }
    if (!dropdownOpen) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(suggestions.length - 1, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(-1, h - 1)); }
    else if (e.key === 'Enter') {
      if (highlight >= 0 && suggestions[highlight]) { e.preventDefault(); navigate(codeHref(suggestions[highlight])); }
      else { setFocused(false); inputRef.current?.blur(); }
    }
  };

  const setExample = useCallback((q: string) => {
    setQuery(q); setDebounced(q); setFamilyFilter('all'); setExpandedKey(null);
    inputRef.current?.focus();
  }, []);

  const perFamilyDefault = hasQuery ? 25 : 10;
  const generatedDate = eager.data?.generatedAt ? new Date(eager.data.generatedAt) : null;
  // Coverage figures come from the stats file header; an older file without
  // the block shows a dash rather than any stand-in number.
  const coverage = eager.data?.coverage || null;
  const hospitalsWithFiles = coverage?.hospitals ? fmtInt(coverage.hospitals) : eager.data ? '—' : null;
  const statesCovered = coverage?.states
    ? `${coverage.states}${coverage.dc ? ' + DC' : ''}`
    : eager.data ? '—' : null;
  const territoriesNote = coverage?.territories?.length
    ? t('procedures:stats.statesSubTerritories', { defaultValue: 'every state with at least one hospital price file, plus {{t}}', t: coverage.territories.join(', ') })
    : t('procedures:stats.statesSub', { defaultValue: 'every state with at least one hospital price file' });

  const pageTitle = t('procedures:meta.pageTitle', { brandName: brand.name });
  const metaDesc = t('procedures:meta.description');
  const canonical = `${brand.primaryDomain}/common-procedures`;

  const detectedFamily = parsed.family ? codeFamily(parsed.family) : null;

  return (
    <div className="w-full min-h-screen" style={{ background: th.pageBg, color: th.ink }}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta name="keywords" content="hospital code lookup, CPT code price, HCPCS code price, DRG price, revenue code, medical bill codes, hospital procedure prices, common medical procedures" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={t('procedures:meta.ogTitle')} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content={brand.name} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('procedures:meta.ogTitle')} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={brand.social.ogImage} />
        {brand.social.twitter && <meta name="twitter:site" content={brand.social.twitter} />}
      </Helmet>

      {/* ── Hero: full-bleed navy band with the search ── */}
      <section
        className="w-full"
        style={{
          background: th.isAhp
            ? `linear-gradient(180deg, ${th.navy} 0%, var(--civic-navy-deep) 100%)`
            : 'linear-gradient(180deg, #111827 0%, #0b1220 100%)',
          borderBottom: `4px solid ${th.isAhp ? th.red : '#374151'}`,
        }}
      >
        <div className="px-4 sm:px-6 lg:px-10 pt-10 pb-8 sm:pt-14 sm:pb-10">
          <p className="text-[11px] sm:text-xs font-bold tracking-[0.22em] uppercase mb-3" style={{ color: th.gold }}>
            {t('procedures:hero.eyebrow', { defaultValue: 'Hospital code lookup' })}
          </p>
          <h1
            className="text-3xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-5xl"
            style={{ fontFamily: th.isAhp ? th.serif : undefined, fontWeight: 400, color: 'var(--civic-cream)' }}
          >
            {t('procedures:hero.title', { defaultValue: 'Look up any code on a hospital bill' })}
          </h1>
          <p className="mt-3 sm:mt-4 max-w-3xl text-sm sm:text-lg leading-relaxed" style={{ color: 'rgba(var(--civic-cream-rgb),0.82)' }}>
            {t('procedures:hero.subtitle', { defaultValue: 'Type the code from your bill or estimate — or just say what it was in plain English. See what U.S. hospitals list for it: cheapest 10%, median, average, priciest 10%, and what the hospitals near you charge.' })}
          </p>

          {/* Search */}
          <div className="relative mt-6 sm:mt-8 max-w-4xl">
            <div
              className="flex items-center rounded-xl overflow-hidden"
              style={{ background: '#FFFFFF', boxShadow: focused ? `0 0 0 3px ${th.gold}, 0 10px 30px rgba(0,0,0,0.35)` : '0 10px 30px rgba(0,0,0,0.35)' }}
            >
              <Search className="ml-4 w-5 h-5 flex-shrink-0" style={{ color: th.navy }} aria-hidden />
              <input
                ref={inputRef}
                type="search"
                inputMode="search"
                autoComplete="off"
                spellCheck={false}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 120)}
                onKeyDown={onKeyDown}
                role="combobox"
                aria-expanded={dropdownOpen}
                aria-controls="code-lookup-suggestions"
                aria-autocomplete="list"
                aria-label={t('procedures:hero.searchAria', { defaultValue: 'Search hospital codes by code or plain-English name' })}
                placeholder={t('procedures:hero.placeholder', { defaultValue: 'Try “99283”, “MRI brain”, “DRG 470”, “knee replacement”…' })}
                className="flex-1 min-w-0 bg-transparent outline-none px-3 py-4 text-base sm:text-lg"
                style={{ color: 'var(--civic-ink)' }}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => { setQuery(''); setDebounced(''); setExpandedKey(null); inputRef.current?.focus(); }}
                  className="mr-2 p-2 rounded-full hover:bg-black/5"
                  aria-label={t('common:common.clear', { defaultValue: 'Clear' })}
                >
                  <X className="w-4 h-4" style={{ color: 'var(--civic-ink)' }} />
                </button>
              )}
              {eager.isFetching && (
                <Loader2 className="mr-4 w-4 h-4 animate-spin flex-shrink-0" style={{ color: th.navy }} aria-hidden />
              )}
            </div>

            {/* Type-ahead */}
            {dropdownOpen && (
              <ul
                id="code-lookup-suggestions"
                role="listbox"
                className="absolute left-0 right-0 z-30 mt-2 rounded-xl overflow-hidden"
                style={{ background: th.cardBg, border: `1px solid ${th.cardBorder}`, boxShadow: '0 16px 40px rgba(0,0,0,0.35)' }}
              >
                {suggestions.map((r, i) => {
                  const fam = codeFamily(r.family);
                  const active = i === highlight;
                  return (
                    <li
                      key={r.key}
                      role="option"
                      aria-selected={active}
                      onMouseDown={e => { e.preventDefault(); navigate(codeHref(r)); }}
                      onMouseEnter={() => setHighlight(i)}
                      className="flex items-center gap-3 px-3 sm:px-4 py-2.5 cursor-pointer"
                      style={{ background: active ? th.rowHover : 'transparent', borderLeft: `4px solid ${fam.color}` }}
                    >
                      <FamilyChip fam={fam} code={r.code} isDark={th.isDark} />
                      <span className="flex-1 min-w-0 truncate text-sm sm:text-base font-medium" style={{ color: th.ink }}>{r.name}</span>
                      <span className="hidden sm:inline text-xs tabular-nums" style={{ color: th.muted }}>
                        {t('procedures:typeahead.median', { defaultValue: 'median' })}{' '}
                        <strong style={{ color: th.median }}>{fmtUsd(r.med)}</strong>
                      </span>
                      <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: th.faint }} aria-hidden />
                    </li>
                  );
                })}
                <li className="px-4 py-2 text-[11px] flex items-center gap-2" style={{ color: th.faint, background: th.rowAlt }}>
                  <CornerDownLeft className="w-3 h-3" aria-hidden />
                  {t('procedures:typeahead.hint', { defaultValue: '↑↓ to choose · Enter opens the code page · results below update as you type' })}
                </li>
              </ul>
            )}
          </div>

          {/* Detected family + examples */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {detectedFamily && (
              <span className="text-xs font-semibold px-2 py-1 rounded-md" style={{ color: 'var(--civic-cream)', background: 'rgba(var(--civic-cream-rgb),0.12)', border: `1px solid ${detectedFamily.color}` }}>
                {t('procedures:hero.detected', { defaultValue: 'Searching {{family}} only', family: detectedFamily.longLabel.split(' — ')[0] })}
              </span>
            )}
            <span className="text-xs mr-1" style={{ color: 'rgba(var(--civic-cream-rgb),0.6)' }}>
              {t('procedures:hero.try', { defaultValue: 'Try:' })}
            </span>
            {['99283', 'MRI brain', 'knee replacement', 'DRG 470', 'REV 0450', 'colonoscopy', 'J1885'].map(ex => (
              <button
                key={ex}
                type="button"
                onClick={() => setExample(ex)}
                className="text-xs sm:text-[13px] px-2.5 py-1 rounded-full transition-colors"
                style={{ color: 'var(--civic-cream)', background: 'rgba(var(--civic-cream-rgb),0.10)', border: '1px solid rgba(var(--civic-cream-rgb),0.28)' }}
              >
                {ex}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stat strip ── */}
      <section className="px-4 sm:px-6 lg:px-10 mt-5 sm:mt-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatTile th={th} label={t('procedures:stats.codes', { defaultValue: 'Codes you can look up' })}
            value={eager.data ? fmtInt(rows.length) : null}
            sub={eager.data ? t('procedures:stats.codesSub', { defaultValue: 'across {{n}} code families', n: Object.keys(familyCounts).length }) : undefined} />
          <StatTile th={th} label={t('procedures:stats.hospitalFiles', { defaultValue: 'Hospitals with published price files' })}
            value={hospitalsWithFiles}
            sub={t('procedures:stats.hospitalFilesSub', { defaultValue: 'we hold each one’s chargemaster; every row shows how many list that code' })} />
          <StatTile th={th} label={t('procedures:stats.states', { defaultValue: 'States covered' })}
            value={statesCovered}
            sub={territoriesNote} />
          <StatTile th={th} label={t('procedures:stats.asOf', { defaultValue: 'Prices as of' })}
            value={generatedDate ? generatedDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : eager.data ? '—' : null}
            sub={t('procedures:stats.asOfSub', { defaultValue: 'gross (sticker) charges from each hospital’s published file' })} />
        </div>
      </section>

      {/* ── Family filter row ── */}
      <section className="px-4 sm:px-6 lg:px-10 mt-6 sm:mt-8">
        <div className="flex flex-wrap items-center gap-2">
          <FilterPill th={th} active={familyFilter === 'all'} onClick={() => { setFamilyFilter('all'); setExpandedKey(null); }}
            label={t('procedures:filter.all', { defaultValue: 'All families' })}
            count={eager.data ? (hasQuery ? matches.length : rows.length) : undefined} />
          {FAMILY_ORDER.map(f => {
            const fam = codeFamily(f);
            const loaded = f !== 'ndc' || (wantNdc && !!ndc.data);
            const count = loaded ? (hasQuery ? familyMatchCounts[f] || 0 : familyCounts[f] || 0) : undefined;
            return (
              <FilterPill
                key={f} th={th} fam={fam}
                active={familyFilter === f}
                onClick={() => {
                  setExpandedKey(null);
                  if (f === 'ndc') setIncludeDrugs(true);
                  setFamilyFilter(familyFilter === f ? 'all' : f);
                }}
                label={fam.label}
                count={count}
                loading={f === 'ndc' && wantNdc && ndc.isFetching}
                hint={f === 'ndc' && !loaded ? t('procedures:filter.loadNdc', { defaultValue: 'tap to load' }) : undefined}
              />
            );
          })}
          <span className="ml-auto text-xs hidden md:inline" style={{ color: th.muted }}>
            {t('procedures:filter.medianRule', { defaultValue: 'Median leads. The average is shown beside it, never alone.' })}
          </span>
        </div>
      </section>

      {/* ── Results ── */}
      <section className="px-4 sm:px-6 lg:px-10 mt-5 sm:mt-6 pb-16 space-y-8">
        {eager.isError && (
          <div className="rounded-xl p-6 sm:p-8 text-center" style={{ background: th.cardBg, border: `1px solid ${th.cardBorder}` }}>
            <p className="text-lg font-semibold" style={{ color: th.priciest }}>{t('procedures:error.title')}</p>
            <p className="mt-1 text-sm" style={{ color: th.muted }}>{t('procedures:error.description')}</p>
            <button type="button" onClick={() => eager.refetch()} className="mt-4 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold" style={{ background: th.navy, color: '#fff' }}>
              <RefreshCw className="w-4 h-4" /> {t('common:common.retry', { defaultValue: 'Try again' })}
            </button>
          </div>
        )}

        {eager.isLoading && !eager.isError && <SkeletonTable th={th} />}

        {eager.data && grouped.length === 0 && (
          <EmptyState th={th} query={debounced} parsed={parsed} familyFilter={familyFilter}
            onClear={() => { setQuery(''); setDebounced(''); setFamilyFilter('all'); }}
            onIncludeDrugs={() => setIncludeDrugs(true)} ndcLoading={wantNdc && ndc.isFetching} ndcLoaded={!!ndc.data} />
        )}

        {eager.data && grouped.map(({ family, rows: famRows }) => {
          const fam = codeFamily(family);
          const limit = showAll[family] ?? perFamilyDefault;
          const visible = famRows.slice(0, limit);
          return (
            <div key={family} id={`family-${family}`}>
              <FamilyHeader th={th} fam={fam} count={famRows.length} hasQuery={hasQuery} />
              <ResultsTable th={th} fam={fam} rows={visible} expandedKey={expandedKey}
                onToggle={k => setExpandedKey(expandedKey === k ? null : k)} t={t} />
              {famRows.length > visible.length && (
                <div className="flex justify-center mt-3">
                  <button
                    type="button"
                    onClick={() => setShowAll(s => ({ ...s, [family]: limit + (hasQuery ? 100 : 40) }))}
                    className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold"
                    style={{ color: th.isDark ? fam.color : fam.ink, border: `1px solid ${fam.color}99`, background: `${fam.color}14` }}
                  >
                    <ChevronDown className="w-4 h-4" />
                    {t('procedures:results.showMore', { defaultValue: 'Show more {{family}} ({{n}} more)', family: fam.label, n: (famRows.length - visible.length).toLocaleString() })}
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {eager.data && !wantNdc && familyFilter === 'all' && (
          <div className="rounded-xl px-4 py-3 flex flex-wrap items-center gap-3 text-sm" style={{ background: th.cardBg, border: `1px dashed ${codeFamily('ndc').color}99` }}>
            <FamilyChip fam={codeFamily('ndc')} isDark={th.isDark} />
            <span style={{ color: th.muted }}>
              {t('procedures:ndc.prompt', { defaultValue: 'Looking for a specific drug product? Drug NDCs are a large index and load on request.' })}
            </span>
            <button type="button" onClick={() => setIncludeDrugs(true)} className="ml-auto font-semibold underline underline-offset-2" style={{ color: th.isDark ? codeFamily('ndc').color : codeFamily('ndc').ink }}>
              {t('procedures:ndc.load', { defaultValue: 'Include drug NDCs' })}
            </button>
          </div>
        )}

        {/* Footnotes: methodology + AMA attribution, as on the CPT page */}
        <div className="pt-6 space-y-2 text-[11px] sm:text-xs leading-relaxed max-w-5xl" style={{ color: th.muted, borderTop: `1px solid ${th.cardBorder}` }}>
          <p>
            <strong style={{ color: th.ink }}>{t('procedures:foot.medianLead', { defaultValue: 'Why the median leads.' })}</strong>{' '}
            {t('procedures:foot.medianBody', { defaultValue: 'Half of hospitals list less than the median, half list more. A handful of extreme prices pull the average up, so the average is never shown on its own — each one carries its gap to the median.' })}{' '}
            <Link to="/data-methodology" className="underline underline-offset-2" style={{ color: th.isDark ? th.gold : th.navy }}>
              {t('procedures:foot.method', { defaultValue: 'How we compute these figures' })}
            </Link>
          </p>
          <p>
            {t('procedures:foot.source', { defaultValue: 'Prices are gross charges — the list price before any insurance or cash discount — taken from each hospital’s own published machine-readable chargemaster. “Cheapest 10%” and “priciest 10%” are the 10th and 90th percentiles across the hospitals reporting the code.' })}
          </p>
          <p>
            {t('procedures:foot.ama', { defaultValue: 'CPT® is a registered trademark of the American Medical Association; we are not affiliated with or endorsed by the AMA, and the consumer-friendly names here are AI-generated from hospitals’ own chargemaster wording, not the AMA’s descriptors. What this page is: each hospital’s own published list prices, organized so you can plan for a procedure and question a bill. What it isn’t: a diagnosis, a treatment recommendation, or a quote — confirm the price with the hospital before you commit.' })}
          </p>
        </div>
      </section>
    </div>
  );
}

// ── Pieces ───────────────────────────────────────────────────────────────────
function StatTile({ th, label, value, sub }: { th: LookupTheme; label: string; value: string | null; sub?: string }) {
  return (
    <div className="rounded-lg p-3 sm:p-4" style={{ background: th.cardBg, border: `1px solid ${th.cardBorder}`, borderTop: `4px solid ${th.isAhp ? th.navy : '#374151'}`, boxShadow: '0 2px 8px rgba(var(--civic-navy-rgb),0.08)' }}>
      <p className="text-[10px] sm:text-[11px] font-bold tracking-[0.14em] uppercase" style={{ color: th.muted }}>{label}</p>
      {value === null ? (
        <div className="mt-2 h-7 w-24 rounded animate-pulse" style={{ background: th.rowAlt }} />
      ) : (
        <p className="mt-1 text-2xl sm:text-3xl font-bold tabular-nums" style={{ color: th.ink, fontFamily: th.isAhp ? th.serif : undefined, fontWeight: th.isAhp ? 400 : 700 }}>{value}</p>
      )}
      {sub && <p className="mt-1 text-[11px] leading-snug" style={{ color: th.faint }}>{sub}</p>}
    </div>
  );
}

function FilterPill({ th, fam, active, onClick, label, count, loading, hint }: {
  th: LookupTheme; fam?: CodeFamily; active: boolean; onClick: () => void; label: string; count?: number; loading?: boolean; hint?: string;
}) {
  const color = fam ? fam.color : (th.isDark ? th.gold : th.navy);
  const text = fam ? (th.isDark ? fam.color : fam.ink) : (th.isDark ? th.gold : th.navy);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors"
      style={{
        color: active ? (th.isDark ? '#0b1220' : '#fff') : text,
        background: active ? color : `${color}${th.isDark ? '1f' : '1a'}`,
        border: `1px solid ${color}${active ? '' : '99'}`,
      }}
    >
      {fam && !active && <span className="inline-block rounded-full" style={{ width: 8, height: 8, background: fam.color }} aria-hidden />}
      {label}
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : count != null ? (
        <span className="tabular-nums text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ background: active ? 'rgba(255,255,255,0.28)' : `${color}33`, color: active ? (th.isDark ? '#0b1220' : '#fff') : text }}>
          {count.toLocaleString()}
        </span>
      ) : hint ? <span className="text-[10px] font-normal opacity-80">{hint}</span> : null}
    </button>
  );
}

function FamilyHeader({ th, fam, count, hasQuery }: { th: LookupTheme; fam: CodeFamily; count: number; hasQuery: boolean }) {
  const { t } = useTranslation(['procedures']);
  return (
    <div className="flex flex-wrap items-end gap-x-4 gap-y-1 mb-3 pl-3" style={{ borderLeft: `5px solid ${fam.color}` }}>
      <h2 className="text-xl sm:text-2xl leading-tight" style={{ fontFamily: th.isAhp ? th.serif : undefined, fontWeight: th.isAhp ? 400 : 700, color: th.ink }}>
        {fam.longLabel}
      </h2>
      <span className="text-xs sm:text-sm tabular-nums" style={{ color: th.muted }}>
        {hasQuery
          ? (count === 1
              ? t('procedures:results.matchOne', { defaultValue: '1 match' })
              : t('procedures:results.matches', { defaultValue: '{{n}} matches', n: count.toLocaleString() }))
          : t('procedures:results.mostReported', { defaultValue: 'most-reported of {{n}} codes', n: count.toLocaleString() })}
      </span>
      {fam.route && (
        <Link to={fam.route} className="text-xs sm:text-sm font-semibold underline underline-offset-2 ml-auto" style={{ color: th.isDark ? fam.color : fam.ink }}>
          {t('procedures:results.browseAll', { defaultValue: 'Browse all {{family}} →', family: fam.label })}
        </Link>
      )}
      <p className="basis-full text-xs sm:text-[13px]" style={{ color: th.muted }}>{fam.blurb}</p>
    </div>
  );
}

function ResultsTable({ th, fam, rows, expandedKey, onToggle, t }: {
  th: LookupTheme; fam: CodeFamily; rows: CodeRow[]; expandedKey: string | null; onToggle: (k: string) => void; t: (k: string, o?: any) => string;
}) {
  const headBg = th.isAhp ? th.navy : '#1f2937';
  const thCls = 'py-2.5 px-3 text-[10px] sm:text-[11px] font-bold tracking-[0.12em] uppercase whitespace-nowrap';
  // Explicit on every <th>: a global light-theme table rule beats the inherited
  // row color and turned the navy header's labels near-invisible.
  // One layout at a time: CSS-hiding the other would still mount it, and an
  // expanded row would then build its hospital panel twice. Cards below 1024px —
  // the nine-column table only fits without sideways scrolling from desktop up.
  const isMobile = useMediaQuery('(max-width: 1023px)');
  return (
    <div className="rounded-xl overflow-hidden" style={{ background: th.cardBg, border: `1px solid ${th.cardBorder}`, boxShadow: '0 2px 8px rgba(var(--civic-navy-rgb),0.06)' }}>
      {/* Desktop table */}
      {!isMobile && (
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', minWidth: 940 }}>
          <colgroup>
            <col style={{ width: 150 }} />
            <col />
            <col style={{ width: 88 }} />
            <col style={{ width: 104 }} />
            <col style={{ width: 104 }} />
            <col style={{ width: 118 }} />
            <col style={{ width: 104 }} />
            <col style={{ width: 70 }} />
            <col style={{ width: 176 }} />
          </colgroup>
          <thead>
            <tr style={{ background: headBg, color: 'var(--civic-cream)' }}>
              <th className={`${thCls} text-left`} style={{ color: 'var(--civic-cream)' }}>{t('procedures:table.code', { defaultValue: 'Code' })}</th>
              <th className={`${thCls} text-left`} style={{ color: 'var(--civic-cream)' }}>{t('procedures:table.what', { defaultValue: 'What it is' })}</th>
              <th className={`${thCls} text-right`} style={{ color: 'var(--civic-cream)' }}>{t('procedures:table.hospitals', { defaultValue: 'Hospitals' })}</th>
              <th className={`${thCls} text-right`} style={{ color: '#9fe0b8' }}>{t('procedures:table.cheapest', { defaultValue: 'Cheapest 10%' })}</th>
              <th className={`${thCls} text-right`} style={{ color: th.gold }}>{t('procedures:table.median', { defaultValue: 'Median' })}</th>
              <th className={`${thCls} text-right`} style={{ color: 'var(--civic-cream)' }}>{t('procedures:table.average', { defaultValue: 'Average' })}</th>
              <th className={`${thCls} text-right`} style={{ color: '#ffb3bc' }}>{t('procedures:table.priciest', { defaultValue: 'Priciest 10%' })}</th>
              <th className={`${thCls} text-right`} style={{ color: 'var(--civic-cream)' }} title={t('procedures:table.spreadTitle', { defaultValue: 'Priciest 10% ÷ cheapest 10% — how far apart hospitals are on this code' })}>
                {t('procedures:table.spread', { defaultValue: 'Spread' })}
              </th>
              <th className={`${thCls} text-right`} style={{ color: 'var(--civic-cream)' }}>{t('procedures:table.nearMe', { defaultValue: 'Near me' })}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const open = expandedKey === r.key;
              const gap = avgGap(r.avg, r.med);
              const spread = r.p10 && r.p90 && r.p10 > 0 ? r.p90 / r.p10 : null;
              return (
                <RowGroup key={r.key}>
                  <tr
                    className="group transition-colors"
                    style={{ background: open ? th.rowHover : i % 2 ? th.rowAlt : 'transparent', borderLeft: `4px solid ${fam.color}` }}
                    onMouseEnter={e => { if (!open) (e.currentTarget as HTMLTableRowElement).style.background = th.rowHover; }}
                    onMouseLeave={e => { if (!open) (e.currentTarget as HTMLTableRowElement).style.background = i % 2 ? th.rowAlt : 'transparent'; }}
                  >
                    <td className="py-2.5 px-3 align-top" style={{ boxShadow: `inset 4px 0 0 ${fam.color}` }}>
                      <Link to={codeHref(r)} className="inline-block hover:opacity-80">
                        <FamilyChip fam={fam} code={r.code} isDark={th.isDark} />
                      </Link>
                    </td>
                    <td className="py-2.5 px-3 align-top min-w-0">
                      <Link to={codeHref(r)} className="font-semibold hover:underline underline-offset-2 block" style={{ color: th.ink }}>{r.name}</Link>
                      {r.wording && (
                        <p className="text-xs italic truncate mt-0.5 min-w-0" style={{ color: th.faint }} title={`${t('procedures:table.wording', { defaultValue: 'How hospitals list it' })}: ${r.wording}`}>
                          “{r.wording}”
                        </p>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums align-top" style={{ color: th.muted }}>{fmtInt(r.th)}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums align-top font-semibold" style={{ color: th.cheapest }}>{fmtUsd(r.p10)}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums align-top font-bold text-base" style={{ color: th.median }}>{fmtUsd(r.med)}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums align-top" style={{ color: th.average }}>
                      <div className="font-semibold">{fmtUsd(r.avg)}</div>
                      {gap && <div className="text-[10px] leading-tight whitespace-nowrap" style={{ color: gap.tone === 'up' ? th.priciest : gap.tone === 'down' ? th.cheapest : th.faint }}>{gap.text}</div>}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums align-top font-semibold" style={{ color: th.priciest }}>{fmtUsd(r.p90)}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums align-top" style={{ color: th.muted }}>{spread ? `${spread >= 10 ? Math.round(spread) : spread.toFixed(1)}×` : '—'}</td>
                    <td className="py-2 px-3 text-right align-top">
                      <button
                        type="button"
                        onClick={() => onToggle(r.key)}
                        aria-expanded={open}
                        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold whitespace-nowrap"
                        style={{ color: open ? '#fff' : (th.isDark ? th.gold : th.navy), background: open ? th.navy : `${th.navy}${th.isDark ? '33' : '12'}`, border: `1px solid ${th.isDark ? th.gold : th.navy}55` }}
                      >
                        <MapPin className="w-3.5 h-3.5" />
                        {t('procedures:table.whatHospitalsCharge', { defaultValue: 'Hospitals near me' })}
                        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                    </td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={9} className="p-0" style={{ boxShadow: `inset 4px 0 0 ${fam.color}` }}>
                        <CodeDetailPanel th={th} row={r} fam={fam} />
                      </td>
                    </tr>
                  )}
                </RowGroup>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {/* Phone cards */}
      {isMobile && (
      <ul className="divide-y" style={{ borderColor: th.cardBorder }}>
        {rows.map(r => {
          const open = expandedKey === r.key;
          const gap = avgGap(r.avg, r.med);
          return (
            <li key={r.key} style={{ borderLeft: `4px solid ${fam.color}`, background: open ? th.rowHover : 'transparent' }}>
              <div className="px-3 py-3">
                <div className="flex items-start gap-2">
                  <Link to={codeHref(r)} className="flex-shrink-0"><FamilyChip fam={fam} code={r.code} isDark={th.isDark} /></Link>
                  <span className="ml-auto text-[11px] tabular-nums whitespace-nowrap" style={{ color: th.muted }}>
                    {fmtInt(r.th)} {t('procedures:card.hospitals', { defaultValue: 'hospitals' })}
                  </span>
                </div>
                <Link to={codeHref(r)} className="block mt-1.5 font-semibold leading-snug" style={{ color: th.ink }}>{r.name}</Link>
                {r.wording && <p className="text-[11px] italic truncate mt-0.5" style={{ color: th.faint }}>“{r.wording}”</p>}
                <div className="mt-2.5 grid grid-cols-4 gap-1.5">
                  <MiniFig label={t('procedures:table.cheapest', { defaultValue: 'Cheapest 10%' })} value={fmtUsd(r.p10)} color={th.cheapest} th={th} />
                  <MiniFig label={t('procedures:table.median', { defaultValue: 'Median' })} value={fmtUsd(r.med)} color={th.median} th={th} lead />
                  <MiniFig label={t('procedures:table.average', { defaultValue: 'Average' })} value={fmtUsd(r.avg)} color={th.average} th={th} sub={gap?.text} />
                  <MiniFig label={t('procedures:table.priciest', { defaultValue: 'Priciest 10%' })} value={fmtUsd(r.p90)} color={th.priciest} th={th} />
                </div>
                <div className="mt-2.5 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onToggle(r.key)}
                    aria-expanded={open}
                    className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold"
                    style={{ color: open ? '#fff' : (th.isDark ? th.gold : th.navy), background: open ? th.navy : `${th.navy}${th.isDark ? '33' : '12'}`, border: `1px solid ${th.isDark ? th.gold : th.navy}55` }}
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    {t('procedures:table.whatHospitalsCharge', { defaultValue: 'Hospitals near me' })}
                    {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                  <Link to={codeHref(r)} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold underline underline-offset-2" style={{ color: th.isDark ? fam.color : fam.ink }}>
                    {t('procedures:card.fullPage', { defaultValue: 'Full page' })} <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
              {open && <CodeDetailPanel th={th} row={r} fam={fam} />}
            </li>
          );
        })}
      </ul>
      )}
    </div>
  );
}

/** React needs a single child list; a fragment keyed per row keeps the expanded
 *  detail row adjacent to its parent without a wrapper element in the table. */
function RowGroup({ children }: { children: ReactNode }) { return <>{children}</>; }

function MiniFig({ label, value, color, th, lead, sub }: { label: string; value: string; color: string; th: LookupTheme; lead?: boolean; sub?: string }) {
  return (
    <div className="rounded-md px-1.5 py-1.5 min-w-0" style={{ background: lead ? `${th.gold}22` : th.rowAlt, border: lead ? `1px solid ${th.gold}88` : `1px solid transparent` }}>
      <p className="text-[9px] font-bold tracking-wider uppercase leading-tight" style={{ color }}>{label}</p>
      <p className={`tabular-nums truncate ${lead ? 'text-sm font-bold' : 'text-[13px] font-semibold'}`} style={{ color }}>{value}</p>
      {sub && <p className="text-[9px] leading-tight" style={{ color: th.faint }}>{sub}</p>}
    </div>
  );
}

// ── Expanded row: the code's price band + hospitals near the reader ─────────
type DetailHospital = CodeDetailHospital & { d?: number };

function CodeDetailPanel({ th, row, fam }: { th: LookupTheme; row: CodeRow; fam: CodeFamily }) {
  const { t } = useTranslation(['procedures']);
  const { location, isLocating, error: locError, detectLocation, lookupZip } = useUserLocation(false);
  const [zip, setZip] = useState('');
  const [zipBusy, setZipBusy] = useState(false);
  const [showLocationForm, setShowLocationForm] = useState(false);

  const detail = useQuery({
    queryKey: ['code-detail-static', row.family, row.code],
    queryFn: () => loadCodeDetail(row.family, row.family === 'drg' ? `MS-${row.code.padStart(3, '0')}` : row.code),
    staleTime: Infinity,
  });

  const d: CodeDetail | null | undefined = detail.data;
  const p = d?.pricing;
  const median = p?.medianGrossCharge || row.med || 0;

  const allHospitals = useMemo<DetailHospital[]>(() => {
    if (!d) return [];
    const list = d.hospitals && d.hospitals.length
      ? d.hospitals
      : [...(d.cheapestHospitals || []), ...(d.mostExpensiveHospitals || [])];
    const seen = new Set<string>();
    return list.filter(h => h && h.p > 0 && h.id && !seen.has(h.id) && seen.add(h.id));
  }, [d]);
  // Detail files cap the per-hospital list (~1,000 rows; legacy files carried
  // only cheapest+priciest). "Nearest" is nearest among what the file holds.
  const reporting = p?.totalHospitals || row.th || 0;
  const truncatedList = !!d && allHospitals.length > 0 && allHospitals.length + 5 < reporting;

  const nearest = useMemo<DetailHospital[]>(() => {
    if (!location) return [];
    return allHospitals
      .filter(h => h.la != null && h.lo != null)
      .map(h => ({ ...h, d: haversineDistance(location.lat, location.lng, h.la as number, h.lo as number) }))
      .sort((a, b) => (a.d as number) - (b.d as number))
      .slice(0, 6);
  }, [allHospitals, location]);

  const cheapest = useMemo(() => [...allHospitals].sort((a, b) => a.p - b.p).slice(0, 3), [allHospitals]);
  const priciest = useMemo(() => [...allHospitals].sort((a, b) => b.p - a.p).slice(0, 3), [allHospitals]);

  const submitZip = async () => {
    const z = zip.trim();
    if (!/^\d{5}$/.test(z)) return;
    setZipBusy(true);
    try { await lookupZip(z); } finally { setZipBusy(false); }
  };

  // Close the "change location" form once a new location actually lands —
  // covers both detect-my-location and ZIP submission, and only fires on
  // success (a failed lookup leaves `location` unchanged, so the form and
  // its error message stay visible).
  useEffect(() => {
    if (location) setShowLocationForm(false);
  }, [location]);

  const vsMedian = (price: number) => {
    if (!median) return '';
    const m = price / median;
    if (m >= 10) return `${Math.round(m)}× median`;
    if (m >= 1.05) return `${m.toFixed(1)}× median`;
    if (m >= 0.95) return '≈ median';
    return `${Math.round((1 - m) * 100)}% below median`;
  };
  const toneFor = (price: number) => (!median ? th.muted : price > median * 1.05 ? th.priciest : price < median * 0.95 ? th.cheapest : th.muted);

  const href = codeHref(row);
  const sectionLabel: CSSProperties = { color: th.muted };

  return (
    <div className="px-3 sm:px-5 py-4 sm:py-5" style={{ background: th.isDark ? 'rgba(0,0,0,0.18)' : 'rgba(var(--civic-navy-rgb),0.035)', borderTop: `1px solid ${th.cardBorder}` }}>
      {detail.isLoading ? (
        <div className="flex items-center gap-2 text-sm" style={{ color: th.muted }}>
          <Loader2 className="w-4 h-4 animate-spin" /> {t('procedures:detail.loading', { defaultValue: 'Loading this code’s hospital list…' })}
        </div>
      ) : !d ? (
        <div className="text-sm" style={{ color: th.muted }}>
          {t('procedures:detail.none', { defaultValue: 'No per-hospital detail file for this code yet.' })}{' '}
          <Link to={href} className="underline underline-offset-2 font-semibold" style={{ color: th.isDark ? fam.color : fam.ink }}>
            {t('procedures:detail.openPage', { defaultValue: 'Open the {{family}} {{code}} page', family: fam.label, code: row.code })}
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-5 lg:gap-8">
          {/* Left: band + national summary */}
          <div className="min-w-0">
            <p className="text-[10px] font-bold tracking-[0.14em] uppercase mb-1" style={sectionLabel}>
              {t('procedures:detail.nationalRange', { defaultValue: 'What U.S. hospitals list — {{n}} reporting', n: fmtInt(p?.totalHospitals || row.th) })}
            </p>
            {d.consumerName && <p className="font-semibold mb-1" style={{ color: th.ink }}>{d.consumerName}</p>}
            {d.llmSummary && <p className="text-sm mb-2 leading-relaxed" style={{ color: th.muted }}>{d.llmSummary}</p>}
            {/* The band's edge labels center on their tick, so give it side room. */}
            <div className="px-6 sm:px-8">
              <PriceRangeBand
                p10={p?.p10GrossCharge ?? row.p10}
                median={p?.medianGrossCharge ?? row.med}
                avg={p?.avgGrossCharge ?? row.avg}
                p90={p?.p90GrossCharge ?? row.p90}
                dark={th.isDark}
                compact
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs" style={{ color: th.muted }}>
              <span>{t('procedures:detail.median', { defaultValue: 'Median' })} <strong style={{ color: th.median }}>{fmtUsd(p?.medianGrossCharge ?? row.med)}</strong></span>
              <span>
                {t('procedures:detail.average', { defaultValue: 'Average' })} <strong style={{ color: th.average }}>{fmtUsd(p?.avgGrossCharge ?? row.avg)}</strong>
                {(() => { const g = avgGap(p?.avgGrossCharge ?? row.avg, p?.medianGrossCharge ?? row.med); return g ? <span className="ml-1" style={{ color: th.faint }}>({g.text})</span> : null; })()}
              </span>
              {p?.bySettings?.filter(s => s.setting !== 'unspecified' && s.medianCharge).slice(0, 2).map(s => (
                <span key={s.setting} className="capitalize">{s.setting} {t('procedures:detail.medianShort', { defaultValue: 'median' })} <strong style={{ color: th.ink }}>{fmtUsd(s.medianCharge)}</strong></span>
              ))}
            </div>
            <Link to={href} className="mt-3 inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold" style={{ background: th.navy, color: '#fff' }}>
              {t('procedures:detail.fullPage', { defaultValue: 'Full {{family}} {{code}} page — every hospital, settings & history', family: fam.label, code: row.code })}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Right: near me */}
          <div className="min-w-0">
            <p className="text-[10px] font-bold tracking-[0.14em] uppercase mb-2 flex items-center flex-wrap gap-x-2" style={sectionLabel}>
              <span>
                <MapPin className="inline w-3.5 h-3.5 -mt-0.5 mr-1" aria-hidden />
                {location
                  ? t('procedures:detail.nearYou', { defaultValue: 'Nearest hospitals to {{place}}', place: location.label || 'you' })
                  : t('procedures:detail.nearMe', { defaultValue: 'Hospitals near me' })}
              </span>
              {location && !showLocationForm && (
                <button
                  type="button"
                  onClick={() => setShowLocationForm(true)}
                  className="normal-case font-normal tracking-normal underline underline-offset-2"
                  style={{ color: th.faint }}
                >
                  {t('procedures:detail.notYou', { defaultValue: 'Not you? Change' })}
                </button>
              )}
            </p>

            {(!location || showLocationForm) && (
              <div className="rounded-lg p-3 sm:p-4" style={{ background: th.cardBg, border: `1px dashed ${th.cardBorder}` }}>
                <p className="text-sm mb-3" style={{ color: th.muted }}>
                  {t('procedures:detail.locatePrompt', { defaultValue: 'Share your location or enter a ZIP to see what the hospitals closest to you list for this code.' })}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => detectLocation()} disabled={isLocating}
                    className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold disabled:opacity-60"
                    style={{ background: th.navy, color: '#fff' }}>
                    {isLocating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                    {t('procedures:detail.useLocation', { defaultValue: 'Use my location' })}
                  </button>
                  <span className="text-xs" style={{ color: th.faint }}>{t('procedures:detail.or', { defaultValue: 'or' })}</span>
                  <input
                    value={zip}
                    onChange={e => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    onKeyDown={e => { if (e.key === 'Enter') submitZip(); }}
                    inputMode="numeric"
                    placeholder={t('procedures:detail.zip', { defaultValue: 'ZIP code' })}
                    aria-label={t('procedures:detail.zip', { defaultValue: 'ZIP code' })}
                    className="w-28 rounded-md px-3 py-2 text-sm outline-none"
                    style={{ background: th.isDark ? '#0b1220' : '#fff', color: th.ink, border: `1px solid ${th.cardBorder}` }}
                  />
                  <button type="button" onClick={submitZip} disabled={zipBusy || !/^\d{5}$/.test(zip)}
                    className="rounded-md px-3 py-2 text-sm font-semibold disabled:opacity-50"
                    style={{ color: th.isDark ? th.gold : th.navy, border: `1px solid ${th.isDark ? th.gold : th.navy}` }}>
                    {zipBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('procedures:detail.go', { defaultValue: 'Go' })}
                  </button>
                  {location && (
                    <button type="button" onClick={() => setShowLocationForm(false)}
                      className="text-xs underline underline-offset-2" style={{ color: th.faint }}>
                      {t('procedures:detail.cancel', { defaultValue: 'Cancel' })}
                    </button>
                  )}
                </div>
                {locError && <p className="mt-2 text-xs" style={{ color: th.priciest }}>{locError}</p>}
              </div>
            )}

            {location && (
              nearest.length ? (
                <ul className="rounded-lg overflow-hidden divide-y" style={{ background: th.cardBg, border: `1px solid ${th.cardBorder}`, borderColor: th.cardBorder }}>
                  {nearest.map(h => (
                    <li key={h.id} className="flex items-center gap-3 px-3 py-2">
                      <Building2 className="w-4 h-4 flex-shrink-0" style={{ color: th.faint }} aria-hidden />
                      <div className="min-w-0 flex-1">
                        <Link to={`/hospital/${h.id}`} className="block font-semibold truncate hover:underline underline-offset-2 text-sm" style={{ color: th.ink }}>{h.n}</Link>
                        <p className="text-[11px] truncate" style={{ color: th.faint }}>
                          {h.c}, {h.s} · {h.d != null ? `${h.d < 10 ? h.d.toFixed(1) : Math.round(h.d)} mi` : ''}{h.st ? ` · ${h.st}` : ''}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold tabular-nums text-sm" style={{ color: th.ink }}>{fmtUsd(h.p)}</p>
                        <p className="text-[10px] tabular-nums" style={{ color: toneFor(h.p) }}>{vsMedian(h.p)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm" style={{ color: th.muted }}>
                  {t('procedures:detail.noneNear', { defaultValue: 'None of the hospitals reporting this code have a mapped location yet.' })}
                </p>
              )
            )}
            {truncatedList && (
              <p className="mt-2 text-[11px]" style={{ color: th.faint }}>
                {t('procedures:detail.truncated', { defaultValue: 'Closest among the {{n}} hospitals in this code’s detail file ({{total}} report it). The complete list is on the code page.', n: fmtInt(allHospitals.length), total: fmtInt(reporting) })}{' '}
                <Link to={href} className="underline underline-offset-2" style={{ color: th.isDark ? fam.color : fam.ink }}>{t('procedures:detail.fullList', { defaultValue: 'See all' })}</Link>
              </p>
            )}

            {/* National extremes — always useful even without a location */}
            {(cheapest.length > 0 || priciest.length > 0) && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ExtremeList th={th} title={t('procedures:detail.cheapestListed', { defaultValue: 'Cheapest listed' })} color={th.cheapest} items={cheapest} vsMedian={vsMedian} />
                <ExtremeList th={th} title={t('procedures:detail.priciestListed', { defaultValue: 'Priciest listed' })} color={th.priciest} items={priciest} vsMedian={vsMedian} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ExtremeList({ th, title, color, items, vsMedian }: { th: LookupTheme; title: string; color: string; items: DetailHospital[]; vsMedian: (p: number) => string }) {
  return (
    <div className="rounded-lg p-3" style={{ background: th.cardBg, border: `1px solid ${th.cardBorder}`, borderTop: `3px solid ${color}` }}>
      <p className="text-[10px] font-bold tracking-[0.14em] uppercase mb-1.5" style={{ color }}>{title}</p>
      <ul className="space-y-1.5">
        {items.map(h => (
          <li key={h.id} className="flex items-baseline gap-2 text-xs">
            <Link to={`/hospital/${h.id}`} className="min-w-0 flex-1 truncate hover:underline underline-offset-2 font-medium" style={{ color: th.ink }} title={`${h.n} — ${h.c}, ${h.s}`}>
              {h.n} <span style={{ color: th.faint }}>({h.s})</span>
            </Link>
            <span className="tabular-nums font-bold whitespace-nowrap" style={{ color: th.ink }}>{fmtUsd(h.p)}</span>
            <span className="tabular-nums whitespace-nowrap text-[10px]" style={{ color }}>{vsMedian(h.p)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SkeletonTable({ th }: { th: LookupTheme }) {
  return (
    <div className="space-y-8" aria-busy="true" aria-live="polite">
      {[0, 1].map(g => (
        <div key={g}>
          <div className="h-7 w-72 rounded animate-pulse mb-3 ml-3" style={{ background: th.rowAlt, borderLeft: `5px solid ${th.cardBorder}` }} />
          <div className="rounded-xl overflow-hidden" style={{ background: th.cardBg, border: `1px solid ${th.cardBorder}` }}>
            <div className="h-9" style={{ background: th.isAhp ? th.navy : '#1f2937', opacity: 0.85 }} />
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-3 py-3" style={{ background: i % 2 ? th.rowAlt : 'transparent' }}>
                <div className="h-5 w-24 rounded animate-pulse" style={{ background: th.rowAlt }} />
                <div className="h-4 flex-1 rounded animate-pulse" style={{ background: th.rowAlt }} />
                {[0, 1, 2, 3].map(k => <div key={k} className="hidden md:block h-4 w-16 rounded animate-pulse" style={{ background: th.rowAlt }} />)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ th, query, parsed, familyFilter, onClear, onIncludeDrugs, ndcLoading, ndcLoaded }: {
  th: LookupTheme; query: string; parsed: ParsedQuery; familyFilter: LookupFamily | 'all';
  onClear: () => void; onIncludeDrugs: () => void; ndcLoading: boolean; ndcLoaded: boolean;
}) {
  const { t } = useTranslation(['procedures']);
  const hasQuery = !!query.trim();
  return (
    <div className="rounded-xl p-6 sm:p-10 text-center" style={{ background: th.cardBg, border: `1px solid ${th.cardBorder}` }}>
      <Search className="w-8 h-8 mx-auto mb-3" style={{ color: th.faint }} aria-hidden />
      <p className="text-lg font-semibold" style={{ color: th.ink, fontFamily: th.isAhp ? th.serif : undefined }}>
        {hasQuery
          ? t('procedures:empty.noMatch', { defaultValue: 'Nothing matched “{{q}}”', q: query.trim() })
          : t('procedures:empty.nothingHere', { defaultValue: 'Nothing to show here yet' })}
      </p>
      <p className="mt-2 text-sm max-w-xl mx-auto" style={{ color: th.muted }}>
        {parsed.codeish
          ? t('procedures:empty.codeHint', { defaultValue: 'Codes look like 99283 (CPT), J1885 (HCPCS), 470 (MS-DRG), 0450 (revenue) or 00409-4888-10 (drug NDC). Leading zeros, spaces and dashes don’t matter.' })
          : t('procedures:empty.textHint', { defaultValue: 'Try a simpler word — “MRI”, “knee”, “ER visit”, “blood test” — or the code from your bill.' })}
      </p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {familyFilter !== 'all' && (
          <button type="button" onClick={onClear} className="rounded-md px-3 py-2 text-sm font-semibold" style={{ background: th.navy, color: '#fff' }}>
            {t('procedures:empty.allFamilies', { defaultValue: 'Search all families' })}
          </button>
        )}
        {!ndcLoaded && (
          <button type="button" onClick={onIncludeDrugs} disabled={ndcLoading} className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold disabled:opacity-60"
            style={{ color: th.isDark ? codeFamily('ndc').color : codeFamily('ndc').ink, border: `1px solid ${codeFamily('ndc').color}` }}>
            {ndcLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('procedures:empty.includeDrugs', { defaultValue: 'Include drug NDCs' })}
          </button>
        )}
        {hasQuery && (
          <Link to={`/search?q=${encodeURIComponent(query.trim())}`} className="rounded-md px-3 py-2 text-sm font-semibold underline underline-offset-2" style={{ color: th.isDark ? th.gold : th.navy }}>
            {t('procedures:empty.siteSearch', { defaultValue: 'Search the whole site instead' })}
          </Link>
        )}
        <button type="button" onClick={onClear} className="rounded-md px-3 py-2 text-sm" style={{ color: th.muted }}>
          {t('procedures:empty.clearFilters')}
        </button>
      </div>
    </div>
  );
}
